import React from 'react';
import { NavLink } from 'react-router-dom';
import { BookMarked, Layers, BookOpen, FileBarChart, Scale, Building2, Users, CalendarDays, Building, LayoutDashboard, Camera, PackageSearch, ClipboardList, ReceiptText } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AppHeader } from './AppHeader';

const SECTIONS = [
  {
    title: 'Transaksi',
    items: [
      { to: '/dashboard',    label: 'Dasbor',               icon: LayoutDashboard, testid: 'nav-dashboard' },
      { to: '/jurnal',       label: 'Jurnal Umum',          icon: BookMarked,      testid: 'nav-jurnal' },
      { to: '/photo-import', label: 'Import Foto Laporan',  icon: Camera,          testid: 'nav-photo-import' },
      { to: '/vendors',      label: 'Vendor & Tagihan',     icon: Building2,       testid: 'nav-vendors' },
      { to: '/payroll',      label: 'Karyawan & Gaji',      icon: Users,           testid: 'nav-payroll' },
      { to: '/reminders',    label: 'Kalender & Pengingat', icon: CalendarDays,    testid: 'nav-reminders' },
    ],
  },
  {
    title: 'Laporan',
    items: [
      { to: '/ledger',   label: 'Buku Besar',   icon: BookOpen,     testid: 'nav-ledger' },
      { to: '/reports',  label: 'Laba Rugi',    icon: FileBarChart, testid: 'nav-reports' },
      { to: '/neraca',   label: 'Neraca',       icon: Scale,        testid: 'nav-neraca' },
    ],
  },
  {
    title: 'Data Master',
    items: [
      { to: '/assets',   label: 'Aset Tetap',      icon: Building, testid: 'nav-assets' },
      { to: '/accounts', label: 'Manajemen Akun',  icon: Layers,   testid: 'nav-accounts' },
    ],
  },
  {
    title: 'Purchase Order',
    items: [
      { to: '/po-import', label: 'PO Import', icon: PackageSearch, testid: 'nav-po-import' },
    ],
  },
  {
    title: 'Operasional',
    items: [
      { to: '/stock-opname', label: 'Stock Opname', icon: ClipboardList, testid: 'nav-stock-opname', opnameAllowed: true },
      { to: '/pajak', label: 'Pajak Marketplace', icon: ReceiptText, testid: 'nav-pajak', pajakAllowed: true },
    ],
  },
];

const filterSections = (user) => {
  if (!user) return [];
  const isAdmin = user.role === 'admin' && !user.opname_only;
  return SECTIONS.map((sec) => ({
    ...sec,
    items: sec.items.filter((it) =>
      isAdmin || (it.opnameAllowed && user.opname_enabled) || (it.pajakAllowed && user.pajak_enabled)),
  })).filter((sec) => sec.items.length > 0);
};

export const AccountingLayout = ({ children, fullWidth = false }) => {
  const { user } = useAuth();
  const visibleSections = filterSections(user);
  const ITEMS = visibleSections.flatMap((s) => s.items);
  return (
    <div className="min-h-screen bg-stone-50">
      <AppHeader />
      <div className={`${fullWidth ? 'max-w-[1600px]' : 'max-w-[1400px]'} mx-auto flex gap-0 md:gap-6 items-start`}>
        {!fullWidth && (
          <aside
            className="hidden md:block sticky top-[64px] w-60 shrink-0 py-8 pl-6"
            data-testid="accounting-sidebar"
          >
            <div className="text-[11px] uppercase tracking-widest text-emerald-800 font-semibold px-2">
              JurnalKu (Akuntansi)
            </div>
            <div className="text-[10px] tracking-wide text-stone-500 px-2 mb-3">
              CV Play Sport Indonesia
            </div>
            <nav className="flex flex-col gap-4">
              {visibleSections.map((sec) => (
                <div key={sec.title} className="flex flex-col gap-1">
                  <div className="text-[10px] uppercase tracking-widest text-stone-400 font-semibold px-3 pt-2">
                    {sec.title}
                  </div>
                  {sec.items.map((it) => (
                    <NavLink
                      key={it.to}
                      to={it.to}
                      data-testid={it.testid}
                      className={({ isActive }) =>
                        `flex items-center gap-3 px-3 py-2.5 rounded-md text-sm font-medium transition-colors ${
                          isActive
                            ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                            : 'text-stone-700 hover:bg-stone-100 border border-transparent'
                        }`
                      }
                    >
                      <it.icon className="w-4 h-4 text-emerald-800" />
                      <span>{it.label}</span>
                    </NavLink>
                  ))}
                </div>
              ))}
            </nav>
          </aside>
        )}

        {/* Mobile tab bar */}
        {!fullWidth && (
          <div className="md:hidden w-full overflow-x-auto border-b border-stone-200 bg-white sticky top-[60px] z-20">
            <div className="flex gap-1 px-4 py-2 min-w-max">
              {ITEMS.map((it) => (
                <NavLink
                  key={it.to}
                  to={it.to}
                  data-testid={`${it.testid}-mobile`}
                  className={({ isActive }) =>
                    `flex items-center gap-2 px-3 py-1.5 rounded-md text-xs font-medium whitespace-nowrap ${
                      isActive
                        ? 'bg-emerald-50 text-emerald-900 border border-emerald-200'
                        : 'text-stone-600 border border-transparent'
                    }`
                  }
                >
                  <it.icon className="w-3.5 h-3.5" />
                  {it.label}
                </NavLink>
              ))}
            </div>
          </div>
        )}

        <main className={`flex-1 min-w-0 py-8 animate-fade-in ${fullWidth ? 'px-6' : 'px-6 md:pr-6 md:pl-0'}`} data-testid="accounting-main">
          {children}
        </main>
      </div>
    </div>
  );
};

export default AccountingLayout;
